import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Building2, User, Mail, Phone, Lock, CheckCircle, ArrowLeft, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TenantSetup() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [passkey, setPasskey] = useState('');
  
  const [formData, setFormData] = useState({
    company_name: '',
    company_code: '',
    address: '',
    city: '',
    state: '',
    country: 'India',
    contact_person: '',
    contact_email: '',
    contact_phone: '',
    username: '',
    password: '',
    confirm_password: ''
  });

  useEffect(() => {
    const storedPasskey = localStorage.getItem('pending_passkey');
    if (!storedPasskey) {
      toast.error('No passkey found. Please start over.');
      navigate('/login');
      return;
    }
    setPasskey(storedPasskey);
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateStep1 = () => {
    if (!formData.company_name || !formData.company_code) {
      toast.error('Company name and code are required');
      return false;
    }
    if (formData.company_code.length < 2 || formData.company_code.length > 10) {
      toast.error('Company code must be 2-10 characters');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.contact_person || !formData.contact_email || !formData.contact_phone) {
      toast.error('All contact details are required');
      return false;
    }
    if (!formData.contact_email.includes('@')) {
      toast.error('Please enter a valid email');
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (!formData.username || !formData.password) {
      toast.error('Username and password are required');
      return false;
    }
    if (formData.username.length < 4) {
      toast.error('Username must be at least 4 characters');
      return false;
    }
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return false;
    }
    if (formData.password !== formData.confirm_password) {
      toast.error('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setStep(3);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep3()) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/register-tenant`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          passkey,
          ...formData
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Registration failed');
      }

      localStorage.setItem('auth_token', data.access_token);
      localStorage.setItem('user_data', JSON.stringify({
        username: data.username,
        role: data.role,
        features: data.features || {}
      }));
      localStorage.removeItem('pending_passkey');

      toast.success('Registration successful! Welcome aboard.');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A2540] via-[#0d3156] to-[#0A2540] flex items-center justify-center p-3 sm:p-4">
      <Card className="w-full max-w-lg p-4 sm:p-6 lg:p-8 bg-white/95 backdrop-blur-sm shadow-2xl">
        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-6 sm:mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-bold text-sm sm:text-base ${
                step >= s ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                {step > s ? <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" /> : s}
              </div>
              {s < 3 && (
                <div className={`w-8 sm:w-12 lg:w-16 h-1 mx-1 sm:mx-2 ${step > s ? 'bg-orange-500' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit}>
          {/* Step 1: Company Info */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="text-center mb-4 sm:mb-6">
                <Building2 className="w-10 h-10 sm:w-12 sm:h-12 text-orange-500 mx-auto mb-2" />
                <h2 className="text-lg sm:text-xl font-bold text-gray-800">Company Details</h2>
                <p className="text-xs sm:text-sm text-gray-500">Tell us about your company</p>
              </div>

              <div>
                <Label className="text-sm">Company Name *</Label>
                <Input
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  placeholder="Your Company Name"
                  className="mt-1"
                />
              </div>

              <div>
                <Label className="text-sm">Company Code * (2-10 chars)</Label>
                <Input
                  name="company_code"
                  value={formData.company_code}
                  onChange={(e) => setFormData(prev => ({ ...prev, company_code: e.target.value.toUpperCase() }))}
                  placeholder="ABC"
                  maxLength={10}
                  className="mt-1 font-mono uppercase"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">City</Label>
                  <Input name="city" value={formData.city} onChange={handleChange} placeholder="City" className="mt-1" />
                </div>
                <div>
                  <Label className="text-sm">State</Label>
                  <Input name="state" value={formData.state} onChange={handleChange} placeholder="State" className="mt-1" />
                </div>
              </div>

              <div>
                <Label className="text-sm">Address</Label>
                <Input name="address" value={formData.address} onChange={handleChange} placeholder="Full Address" className="mt-1" />
              </div>

              <Button type="button" onClick={handleNext} className="w-full bg-orange-500 hover:bg-orange-600">
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {/* Step 2: Contact Info */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="text-center mb-4 sm:mb-6">
                <User className="w-10 h-10 sm:w-12 sm:h-12 text-orange-500 mx-auto mb-2" />
                <h2 className="text-lg sm:text-xl font-bold text-gray-800">Contact Details</h2>
                <p className="text-xs sm:text-sm text-gray-500">Primary contact person</p>
              </div>

              <div>
                <Label className="text-sm">Contact Person Name *</Label>
                <Input
                  name="contact_person"
                  value={formData.contact_person}
                  onChange={handleChange}
                  placeholder="Full Name"
                  className="mt-1"
                />
              </div>

              <div>
                <Label className="text-sm">Email *</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    name="contact_email"
                    type="email"
                    value={formData.contact_email}
                    onChange={handleChange}
                    placeholder="email@company.com"
                    className="pl-10"
                  />
                </div>
              </div>

              <div>
                <Label className="text-sm">Phone *</Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    name="contact_phone"
                    value={formData.contact_phone}
                    onChange={handleChange}
                    placeholder="+91 98765 43210"
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={() => setStep(1)} className="flex-1">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
                <Button type="button" onClick={handleNext} className="flex-1 bg-orange-500 hover:bg-orange-600">
                  Continue <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Account Credentials */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="text-center mb-4 sm:mb-6">
                <Lock className="w-10 h-10 sm:w-12 sm:h-12 text-orange-500 mx-auto mb-2" />
                <h2 className="text-lg sm:text-xl font-bold text-gray-800">Create Your Account</h2>
                <p className="text-xs sm:text-sm text-gray-500">Set your login credentials</p>
              </div>

              <div>
                <Label className="text-sm">Username * (min 4 characters)</Label>
                <Input
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder="Choose a username"
                  className="mt-1"
                />
              </div>

              <div>
                <Label className="text-sm">Password * (min 6 characters)</Label>
                <Input
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Create a password"
                  className="mt-1"
                />
              </div>

              <div>
                <Label className="text-sm">Confirm Password *</Label>
                <Input
                  name="confirm_password"
                  type="password"
                  value={formData.confirm_password}
                  onChange={handleChange}
                  placeholder="Confirm your password"
                  className="mt-1"
                />
              </div>

              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={() => setStep(2)} className="flex-1">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
                <Button type="submit" disabled={loading} className="flex-1 bg-green-600 hover:bg-green-700">
                  {loading ? 'Creating...' : 'Complete Setup'} <CheckCircle className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}
        </form>

        <p className="text-center text-xs text-gray-400 mt-4 sm:mt-6">
          Your passkey: <span className="font-mono bg-gray-100 px-2 py-0.5 rounded">{passkey}</span>
        </p>
      </Card>
    </div>
  );
}
